import { DateInput } from '@/components/form/DateInput';
import { MIN_DATE } from '@/components/form/dateConstants';
import { CalendarIcon } from '@/components/icons/CalendarIcon';
import { FormControl, FormField, FormItem } from '@/components/primitives/Form';
import { useFormField } from '@/components/primitives/useFormField';
import { useTranslation } from '@/i18n';
import { cn } from '@/utils/cn';
import { calendarDateToIso, isoToCalendarDate } from '@/utils/dateTime';
import { getBcp47Locale } from '@/utils/locale';
import {
  getLocalTimeZone,
  today,
  type CalendarDate,
  type DateValue,
} from '@internationalized/date';
import { Button, Calendar, Popover } from '@packages/webshop-design-system';
import React from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';

interface DatePickerFieldContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<DatePickerFieldProps<TFieldValues, TName>, 'name'> {
  field: ControllerRenderProps<TFieldValues, TName>;
}

const DatePickerFieldContent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field,
  label,
  hideLabel,
  'aria-label': ariaLabel,
  classNames,
  required,
  isDateUnavailable,
  maxValue,
}: DatePickerFieldContentProps<TFieldValues, TName>) => {
  const { error } = useFormField();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const todayDate = today(getLocalTimeZone());
  const resolvedMaxValue = maxValue ?? todayDate;
  const resolvedIsDateUnavailable =
    isDateUnavailable ??
    ((date: DateValue) => date.compare(resolvedMaxValue) > 0);
  const selectedCalendarDate = isoToCalendarDate(
    typeof field.value === 'string' ? field.value : undefined
  );

  return (
    <FormItem className={classNames?.wrapper}>
      <FormControl label={label} required={required} hideLabel={hideLabel}>
        <DateInput
          value={typeof field.value === 'string' ? field.value : undefined}
          onChange={field.onChange}
          onBlur={field.onBlur}
          maxValue={resolvedMaxValue}
          error={!!error}
          aria-label={ariaLabel}
          classNames={{ input: cn('pr-10', classNames?.trigger) }}
          data-testid="date-picker-input"
          endAdornment={
            <Popover
              isOpen={open}
              onOpenChange={setOpen}
              placement="bottom end"
              trigger={
                <Button
                  variant="icon"
                  size="small"
                  aria-label={t('text.pickADate')}
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              }
              content={
                <Calendar
                  data-testid="date-picker-calendar"
                  value={selectedCalendarDate ?? null}
                  defaultFocusedValue={selectedCalendarDate}
                  onChange={(date) => {
                    if (date) {
                      field.onChange(calendarDateToIso(date));
                    }
                    setOpen(false);
                  }}
                  pickers={true}
                  locale={getBcp47Locale()}
                  isDateUnavailable={resolvedIsDateUnavailable}
                  // Real bounds plus a spanning window give one stable year
                  // list (1900..max) instead of a re-centering 50-year slice
                  minValue={MIN_DATE}
                  maxValue={resolvedMaxValue}
                  visibleYears={resolvedMaxValue.year - MIN_DATE.year + 1}
                />
              }
              data-testid="date-picker-popover"
              className={{ content: 'w-auto p-4' }}
            />
          }
        />
      </FormControl>
    </FormItem>
  );
};

export interface DatePickerFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> {
  label: React.ReactNode;
  name: TName;
  hideLabel?: boolean;
  /** Plain-text label for the date segments (the visible label is a ReactNode) */
  'aria-label'?: string;
  classNames?: { wrapper?: string; trigger?: string };
  required?: boolean;
  isDateUnavailable?: (date: DateValue) => boolean;
  maxValue?: CalendarDate;
}

export const DatePickerField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  name,
  ...props
}: DatePickerFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <DatePickerFieldContent field={field} {...props} />
      )}
    />
  );
};

DatePickerField.displayName = 'DatePickerField';
